import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { getStyles } from './styles';
import { Autocomplete, Box, Button, Checkbox, FormControl, FormControlLabel, Icon, InputAdornment, Popover, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { SketchPicker } from 'react-color';
import { useAppContext } from '../../../../context/AppContext';
import LoadBar from '../../../../utils/LoadBar';
import { IconSvg } from '../../../../utils/globalIcons';
import { ReactSVG } from 'react-svg';

function ChatbotBranding({ dataLoading, action_type, onSave, onCancel, onNext }) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const { setAppDetails, appDetails } = useAppContext();

    const [formErrors, setFormErrors] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedKey, setSelectedKey] = useState(null);
    const Font_families = ['Inter', 'Roboto', 'Helvetica', 'Arial', 'Lora', 'Times New Roman', 'Georgia', 'Lato']
    const chat_launch_icons = ['chatlaunch1', 'chatlaunch2', 'chatlaunch3', 'chatlaunch4'];

    const handleClick = (event, key) => {
        setAnchorEl(event.currentTarget);
        setSelectedKey(key);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedKey(null);
    };

    useEffect(() => {
        const styles = appDetails.chatStylesData;
        const errors = { ...formErrors };

        Object.keys(errors).forEach((key) => {
            if (styles?.[key] && styles[key].toString().trim()) {
                delete errors[key];
            }
        });

        if (formErrors?.feedback_url && appDetails.chatFeedbackData?.feedback_url?.trim()) { delete errors?.feedback_url }

        if (JSON.stringify(errors) !== JSON.stringify(formErrors)) {
            setFormErrors(errors);
        }
    }, [appDetails.chatStylesData, appDetails.chatFeedbackData, formErrors]);

    const handleNext = () => {
        const styles = appDetails.chatStylesData;

        const requiredFields = ["font_family", "font_color", "background_color", "surface_color", "surface_border",
            "button_fill", "button_text", "question_bubble_color", "question_bubble_font_color",
        ];

        let errors = requiredFields.reduce((acc, field) => {
            if (!styles?.[field]?.toString().trim()) {
                acc[field] = "This field is required";
            }
            return acc;
        }, {});

        const feedback_status = appDetails?.chatFeedbackData?.feedback_status === "true";
        const feedback_url = appDetails?.chatFeedbackData?.feedback_url ?? "";
        if (feedback_status && !feedback_url.trim()) {
            errors.feedback_url = 'This filed is required'
        }

        if (Object.keys(errors).length === 0) {
            onNext('security')
        } else {
            setFormErrors(errors);
        }
    };

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>
                {dataLoading && <LoadBar />}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>Branding</Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            sx={{ backgroundColor: '#0B51C5', borderRadius: '20px', width: '100px' }}
                            onClick={() => { action_type === "update" ? onSave('branding') : handleNext() }}
                            disabled={dataLoading}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
                                {action_type === "update" ? 'SAVE' : 'NEXT'}
                            </Typography>
                        </Button>

                        <Button
                            onClick={onCancel}
                            sx={{ backgroundColor: '#DEDEDE', borderRadius: '20px', width: '100px' }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#404040' }}>
                                CANCEL
                            </Typography>
                        </Button>
                    </Box>
                </Box>

                <Box marginTop={'30px'}>
                    <Box className="font_styling" sx={{ display: 'flex', justifyContent: 'row', alignItems: 'center' }}>
                        <Box>
                            <FormControl>
                                <Typography sx={{ ...styles.paraText, marginBottom: '6px' }}>Font Family</Typography>
                                <Autocomplete
                                    size="small"
                                    sx={{ width: '200px' }}
                                    options={Font_families}
                                    getOptionLabel={(option) => option || ""}
                                    id="clear-on-escape"
                                    clearOnEscape
                                    value={Font_families.find((option) => option === appDetails.chatStylesData.font_family) || null}
                                    renderInput={(params) => (
                                        <TextField {...params} variant="outlined" placeholder="Font Family" />
                                    )}
                                    onChange={(event, value) => setAppDetails((prev) => ({ ...prev, chatStylesData: { ...prev.chatStylesData, font_family: value || null } }))}
                                />
                                {formErrors.font_family && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.font_family}</Typography>}
                            </FormControl>
                        </Box>
                        <Box marginLeft={'30px'}>
                            <FormControl>
                                <Typography sx={{ ...styles.paraText, marginBottom: '6px' }}>Font Color</Typography>
                                <TextField
                                    size="small"
                                    value={appDetails.chatStylesData.font_color}
                                    onClick={(e) => handleClick(e, 'font_color')}
                                    sx={{ width: '200px' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <div
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        backgroundColor: appDetails.chatStylesData.font_color,
                                                        borderRadius: '4px',
                                                        border: '0.5px solid grey'
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                        readOnly: true,
                                    }}
                                    fullWidth
                                />
                                {formErrors.font_color && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.font_color}</Typography>}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className="background_styling" sx={{ display: 'flex', justifyContent: 'row', alignItems: 'center', marginTop: '25px' }}>
                        <Box>
                            <FormControl>
                                <Typography sx={{ ...styles.paraText, marginBottom: '6px' }}>Background Color</Typography>
                                <TextField
                                    size="small"
                                    value={appDetails.chatStylesData.background_color}
                                    onClick={(e) => handleClick(e, 'background_color')}
                                    sx={{ width: '200px' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <div
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        backgroundColor: appDetails.chatStylesData.background_color,
                                                        borderRadius: '4px',
                                                        border: '0.5px solid grey'
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                        readOnly: true,
                                    }}
                                    fullWidth
                                />
                                {formErrors.background_color && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.background_color}</Typography>}
                            </FormControl>
                        </Box>

                        <Box marginLeft={'30px'}>
                            <FormControl>
                                <Typography sx={{ ...styles.paraText, marginBottom: '6px' }}>Surface Color</Typography>
                                <TextField
                                    size="small"
                                    value={appDetails.chatStylesData.surface_color}
                                    onClick={(e) => handleClick(e, 'surface_color')}
                                    sx={{ width: '200px' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <div
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        backgroundColor: appDetails.chatStylesData.surface_color,
                                                        borderRadius: '4px',
                                                        border: '0.5px solid grey'
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                        readOnly: true,
                                    }}
                                    fullWidth
                                />
                                {formErrors.surface_color && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.surface_color}</Typography>}

                            </FormControl>
                        </Box>

                        <Box marginLeft={'30px'}>
                            <FormControl>
                                <Typography sx={{ ...styles.paraText, marginBottom: '6px' }}>Surface Border</Typography>
                                <TextField
                                    size="small"
                                    value={appDetails.chatStylesData.surface_border}
                                    onClick={(e) => handleClick(e, 'surface_border')}
                                    sx={{ width: '200px' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <div
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        backgroundColor: appDetails.chatStylesData.surface_border,
                                                        borderRadius: '4px',
                                                        border: '0.5px solid grey'
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                        readOnly: true,
                                    }}
                                    fullWidth
                                />
                                {formErrors.surface_border && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.surface_border}</Typography>}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className="button_styling" sx={{ display: 'flex', justifyContent: 'row', alignItems: 'center', marginTop: '25px' }}>

                        <Box>
                            <FormControl>
                                <Typography sx={{ ...styles.paraText, marginBottom: '6px' }}>Button Fill</Typography>
                                <TextField
                                    size="small"
                                    value={appDetails.chatStylesData.button_fill}
                                    onClick={(e) => handleClick(e, 'button_fill')}
                                    sx={{ width: '200px' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <div
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        backgroundColor: appDetails.chatStylesData.button_fill,
                                                        borderRadius: '4px',
                                                        border: '0.5px solid grey'
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                        readOnly: true,
                                    }}
                                    fullWidth
                                />
                                {formErrors.button_fill && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.button_fill}</Typography>}
                            </FormControl>
                        </Box>

                        <Box marginLeft={'30px'}>
                            <FormControl>
                                <Typography sx={{ ...styles.paraText, marginBottom: '6px' }}>Button Text</Typography>
                                <TextField
                                    size="small"
                                    value={appDetails.chatStylesData.button_text}
                                    onClick={(e) => handleClick(e, 'button_text')}
                                    sx={{ width: '200px' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <div
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        backgroundColor: appDetails.chatStylesData.button_text,
                                                        borderRadius: '4px',
                                                        border: '0.5px solid grey'
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                        readOnly: true,
                                    }}
                                    fullWidth
                                />
                                {formErrors.button_text && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.button_text}</Typography>}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className="question_bubble" sx={{ display: 'flex', justifyContent: 'row', alignItems: 'center', marginTop: '25px' }}>
                        <Box>
                            <FormControl>
                                <Typography sx={{ ...styles.paraText, marginBottom: '6px' }}>Question Bubble Color</Typography>
                                <TextField
                                    size="small"
                                    value={appDetails.chatStylesData.question_bubble_color}
                                    onClick={(e) => handleClick(e, 'question_bubble_color')}
                                    sx={{ width: '200px' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <div
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        backgroundColor: appDetails.chatStylesData.question_bubble_color,
                                                        borderRadius: '4px',
                                                        border: '0.5px solid grey'
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                        readOnly: true,
                                    }}
                                    fullWidth
                                />
                                {formErrors.question_bubble_color && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.question_bubble_color}</Typography>}
                            </FormControl>
                        </Box>

                        <Box marginLeft={'30px'}>
                            <FormControl>
                                <Typography sx={{ ...styles.paraText, marginBottom: '6px' }}>Question Bubble Font Color</Typography>
                                <TextField
                                    size="small"
                                    value={appDetails.chatStylesData.question_bubble_font_color}
                                    onClick={(e) => handleClick(e, 'question_bubble_font_color')}
                                    sx={{ width: '200px' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <div
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        backgroundColor: appDetails.chatStylesData.question_bubble_font_color,
                                                        borderRadius: '4px',
                                                        border: '0.5px solid grey'
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                        readOnly: true,
                                    }}
                                    fullWidth
                                />
                                {formErrors.question_bubble_font_color && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.question_bubble_font_color}</Typography>}
                            </FormControl>
                        </Box>

                    </Box>

                    {appDetails?.app_bot_type === "simple_bot" && <Box sx={{ marginTop: '25px' }}>
                        <Typography sx={{ ...styles.paraText, marginBottom: '6px' }}>Launch Icon</Typography>
                        <Box
                            sx={{
                                bgcolor: "#FFFFFF",
                                borderRadius: "5px",
                                border: "1px solid #DEDEDE",
                                cursor: "pointer",
                                width: "500px",
                                p: 1.5,
                                mb: 1
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}
                            >
                                {chat_launch_icons.map((icon, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 36,
                                            height: 36,
                                            borderRadius: '50%',
                                            backgroundColor: '#FFFFFF',
                                            boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.1)',
                                            border: `1px solid ${appDetails?.app_launch_icon === icon ? '#0B51C5' : 'transparent'}`,
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => setAppDetails({ ...appDetails, app_launch_icon: icon })}
                                    >
                                        <ReactSVG
                                            src={{
                                                chatlaunch1: IconSvg.chatlaunch1Icon,
                                                chatlaunch2: IconSvg.chatlaunch2Icon,
                                                chatlaunch3: IconSvg.chatlaunch3Icon,
                                                chatlaunch4: IconSvg.chatlaunch4Icon
                                            }[icon]}
                                            beforeInjection={(svg) => {
                                                svg.setAttribute('style', 'width:18px; height:18px; display:block;');
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>}

                    <Box sx={{ marginTop: '25px' }}>
                        <Typography sx={{ ...styles.paraText }} mb={0.5}>Powered by Logo</Typography>
                        <Box
                            sx={{
                                bgcolor: "#FFFFFF",
                                borderRadius: "5px",
                                border: "1px solid #DEDEDE",
                                cursor: "pointer",
                                width: "500px",
                                p: 1.5,
                                mb: 1
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Checkbox
                                    checked={appDetails.chatStylesData.poweredby_logo_status}
                                    onChange={(e) =>
                                        setAppDetails((prev) => ({
                                            ...prev,
                                            chatStylesData: { ...prev.chatStylesData, poweredby_logo_status: e.target.checked },
                                        }))
                                    }
                                />
                                <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>
                                    Remove “Powered by Ameya” Logo
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ marginTop: '25px' }}>
                        <Typography mb={0.5}>User Feedback</Typography>
                        <Box
                            sx={{
                                bgcolor: "#FFFFFF",
                                borderRadius: "5px",
                                border: "1px solid #DEDEDE",
                                cursor: "pointer",
                                width: "500px",
                                p: 1.5,
                                mb: 1
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Typography>Enable User Feedback</Typography>
                                <RadioGroup
                                    aria-labelledby="demo-radio-buttons-group-label"
                                    value={appDetails?.chatFeedbackData?.feedback_status}
                                    onChange={(e) => setAppDetails((prev) => ({ ...prev, chatFeedbackData: { ...prev.chatFeedbackData, feedback_status: e.target.value } }))}
                                    style={{ display: 'flex', flexDirection: 'row' }}
                                >
                                    <FormControlLabel value={"true"} control={<Radio color='#59A292' />} label="Yes" />
                                    <FormControlLabel value={"false"} control={<Radio color='#59A292' />} label="No" />
                                </RadioGroup>
                            </Box>

                            {appDetails?.chatFeedbackData?.feedback_status === "true" &&
                                <FormControl sx={{ marginTop: '5px' }}>
                                    <Typography mb={0.5}>Feedback URL <span style={{ color: 'red' }}>*</span></Typography>
                                    <TextField
                                        variant="outlined"
                                        type="url"
                                        size='small'
                                        sx={{ width: '450px' }}
                                        value={appDetails?.chatFeedbackData?.feedback_url ?? ""}
                                        onChange={(e) => setAppDetails((prev) => ({ ...prev, chatFeedbackData: { ...prev.chatFeedbackData, feedback_url: e.target.value } }))}
                                    />
                                    {formErrors.feedback_url && <Typography sx={styles.errorText}>{formErrors.feedback_url}</Typography>}
                                </FormControl>
                            }

                        </Box>
                    </Box>

                </Box>

                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                >
                    {selectedKey && (
                        <SketchPicker
                            color={appDetails.chatStylesData[selectedKey]}
                            onChange={(color) => {
                                setAppDetails((prev) => ({
                                    ...prev,
                                    chatStylesData: {
                                        ...prev.chatStylesData,
                                        [selectedKey]: color.hex,
                                    },
                                }));
                            }}
                        />
                    )}
                </Popover>

            </Box>
        </Box>
    )
}

export default ChatbotBranding;